import React, { useMemo } from 'react';
import type { SequencerPresentation } from '../../core/print/sequencerPresentation';
import { sequencerStepMarker } from '../../core/print/sequencerPresentation';

export interface SequencerTimelineProps {
  items: string[];
  order: number[];
  size?: 'md' | 'lg';
  editable?: boolean;
  onItemChange?: (itemIndex: number, text: string) => void;
  presentation?: SequencerPresentation;
}

const PER_ROW_THRESHOLD = 5;
const MAX_PER_ROW = 5;

/** Neo-Swiss card tones — left accent stripe only */
const STEP_CARD_ACCENTS = ['#5b21b6', '#047857', '#1d4ed8', '#a16207'] as const;

function cardTilt(position: number): number {
  const tilts = [0.35, -0.3, 0.4, -0.25, 0.3, -0.35, 0.28, -0.4, 0.32, -0.28];
  return tilts[position % tilts.length] ?? 0.3;
}

function chunkOrder(order: number[], perRow: number): number[][] {
  const rows: number[][] = [];
  for (let i = 0; i < order.length; i += perRow) {
    rows.push(order.slice(i, i + perRow));
  }
  return rows;
}

function StepCard({
  text,
  position,
  editable,
  onItemChange,
  itemIdx,
  presentation = 'default',
}: {
  text: string;
  position: number;
  editable: boolean;
  onItemChange?: (itemIndex: number, text: string) => void;
  itemIdx: number;
  presentation?: SequencerPresentation;
}) {
  const accent = STEP_CARD_ACCENTS[position % STEP_CARD_ACCENTS.length];
  const tilt = cardTilt(position);
  const marker = sequencerStepMarker(presentation, position);

  return (
    <div className="seq-flow__step">
      <div
        className={`seq-flow__dot${presentation === 'history' ? ' seq-flow__dot--history' : ''}${presentation === 'music' ? ' seq-flow__dot--music' : ''}`}
        aria-hidden
      >
        <span>{marker}</span>
      </div>
      <div
        className="seq-flow__card"
        style={{
          borderLeftWidth: '4px',
          borderLeftColor: accent,
          transform: `rotate(${tilt}deg)`,
        }}
      >
        {editable && onItemChange ? (
          <textarea
            className="seq-flow__input canvas-inline-input"
            rows={3}
            value={text}
            placeholder={`Paso ${position + 1}`}
            onChange={(e) => onItemChange(itemIdx, e.target.value)}
          />
        ) : (
          <p className="seq-flow__label" title={text}>
            {text || '…'}
          </p>
        )}
      </div>
    </div>
  );
}

export function SequencerTimeline({
  items,
  order,
  size = 'md',
  editable = false,
  onItemChange,
  presentation = 'default',
}: SequencerTimelineProps) {
  const count = order.length;
  const perRow = count > PER_ROW_THRESHOLD ? MAX_PER_ROW : Math.max(count, 1);
  const multiRow = count > PER_ROW_THRESHOLD;

  const rows = useMemo(() => chunkOrder(order, perRow), [order, perRow]);

  return (
    <div
      className={[
        'seq-flow w-full',
        multiRow ? 'seq-flow--multi' : '',
        size === 'lg' ? 'seq-flow--lg' : '',
        editable ? 'seq-flow--editable' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="seq-flow__scroller">
        <div className="seq-flow__lanes">
          {rows.map((rowSlice, rowIdx) => {
            const rowStart = rowIdx * perRow;

            return (
              <div key={rowIdx} className="seq-flow__lane">
                <div className="seq-flow__thread" aria-hidden />
                <div className="seq-flow__row">
                  {rowSlice.map((itemIdx, col) => {
                    const position = rowStart + col;
                    const text = items[itemIdx] ?? '';
                    return (
                      <StepCard
                        key={`${position}-${itemIdx}`}
                        text={text}
                        position={position}
                        itemIdx={itemIdx}
                        editable={editable}
                        onItemChange={onItemChange}
                        presentation={presentation}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
